import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import { initializePushNotifications, getNotificationSettings } from "@/lib/pushNotifications";
import ScrollRestoration from "@/components/common/ScrollRestoration";
import { useAppBadge } from "@/hooks/useAppBadge";

// Pages
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Pipeline from "@/pages/Pipeline";
import Prospects from "@/pages/Prospects";


import ProspectForm from "@/pages/ProspectForm";
import ProspectDetail from "@/pages/ProspectDetail";
import TeamsV2 from "@/pages/TeamsV2";
import OnboardingSignup from "@/pages/OnboardingSignup";
import OnboardingAdmin from "@/pages/OnboardingAdmin";
import NewsFeed from "@/pages/NewsFeed";
import IdeaBox from "@/pages/IdeaBox";
import Settings from "@/pages/Settings";
import Invoices from "@/pages/Invoices";
import BillingV2 from "@/pages/BillingV2";
import CommissionLedger from "@/pages/CommissionLedger";
import Tasks from "@/pages/Tasks";
import CalendarPage from "@/pages/CalendarPage";
import ComingSoon from "@/pages/ComingSoon";
import AccountantLogin from "@/pages/AccountantLogin";
import AccountantDashboard from "@/pages/AccountantDashboard";
// StatsCommerciales removed - now redirects to Dashboard
import SupportPage from "@/pages/SupportPage";
import CommunicationPage from "@/pages/CommunicationPage";
import Clients from "@/pages/Clients";
import Academy from "@/pages/academy/Academy";
import ModuleDetail from "@/pages/academy/ModuleDetail";
import AcademyAdmin from "@/pages/academy/AcademyAdmin";
import AcademySupervision from "@/pages/academy/AcademySupervision";
import AcademyManage from "@/pages/academy/AcademyManage";
import Reports from "@/pages/Reports";
import SharedPresentation from "@/pages/SharedPresentation";
import EmailPreview from "@/pages/EmailPreview";
import FormsCEEPage from "@/pages/FormsCEEPage";
import StudyFormPage from "@/pages/StudyFormPage";
import OutilsLiensUtilesPage from "@/pages/OutilsLiensUtilesPage";
import UserProfile from "@/pages/UserProfile";
import Implication from "@/pages/Implication";
// Inside VNX — réseau social interne (chargé à la demande pour ne pas alourdir le CRM)
const InsideVNX = lazy(() => import("@/pages/social/InsideVNX"));
// MessengerWidget retiré - VTchatButton est utilisé dans DashboardLayout

// Protected Route wrapper
const TRAINING_ALLOWED_PREFIXES = ['/academy', '/support'];

// Déploiement dédié Inside VNX (vnx.valnexo.fr / inside.valnexo.fr) :
// l'app doit s'ouvrir ET rester sur Inside VNX (pas sur le CRM).
const IS_VNX_HOST = typeof window !== 'undefined' && /^(vnx|inside)\./i.test(window.location.hostname);

const ProtectedRoute = ({ children }) => {
    const { user, loading, isTrainingRestricted, isApprenant, isCommunity } = useAuth();
    const location = useLocation();

    // Badge numérique de notifications sur l'icône de l'app (PWA installée)
    useAppBadge();

    // Initialize push notifications when user is authenticated
    useEffect(() => {
        if (user) {
            const settings = getNotificationSettings();
            if (settings.enabled) {
                initializePushNotifications().catch(console.error);
            }
        }
    }, [user]);
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
            </div>
        );
    }
    
    if (!user) {
        return <Navigate to="/login" replace />;
    }
    
    // Apprenant (candidat onboarding) ou apporteur insuffisamment formé :
    // accès limité à l'Académie + Support uniquement → sinon redirection /academy
    if (isApprenant() || isTrainingRestricted()) {
        const allowed = TRAINING_ALLOWED_PREFIXES.some(p => location.pathname.startsWith(p));
        if (!allowed) {
            return <Navigate to="/academy" replace />;
        }
    }

    // Community Manager : accès Inside VNX uniquement (aucun accès CRM)
    if (isCommunity() && !location.pathname.startsWith('/inside-vnx')) {
        return <Navigate to="/inside-vnx" replace />;
    }

    return children;
};

// Public Route wrapper (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
    const { user, loading, isApprenant, isCommunity } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
            </div>
        );
    }

    if (user) {
        const to = isApprenant() ? '/academy' : (isCommunity() || IS_VNX_HOST ? '/inside-vnx' : '/dashboard');
        return <Navigate to={to} replace />;
    }

    return children;
};

function AppRoutes() {
    // Sous-domaine dédié (ex. vnx.valnexo.fr) → ouvre directement sur Inside VNX
    const defaultPath = IS_VNX_HOST ? '/inside-vnx' : '/dashboard';
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/login" element={
                <PublicRoute>
                    <Login />
                </PublicRoute>
            } />
            
            {/* Mot de passe oublié - Public */}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Inscription publique au parcours de formation (lien à diffuser sur le site public) */}
            <Route path="/formation" element={
                <PublicRoute>
                    <OnboardingSignup />
                </PublicRoute>
            } />
            
            {/* Page de partage publique - Pas d'authentification */}
            <Route path="/share/:token" element={<SharedPresentation />} />
            
            {/* Email Preview - Pour démonstration */}
            <Route path="/email-preview" element={
                <ProtectedRoute>
                    <EmailPreview />
                </ProtectedRoute>
            } />

            {/* Protected Routes */}
            <Route path="/dashboard" element={
                <ProtectedRoute>
                    <Dashboard />
                </ProtectedRoute>
            } />

            {/* Fil d'actualité - page dédiée (sorti du Dashboard pour la fluidité) */}
            <Route path="/feed" element={
                <ProtectedRoute>
                    <NewsFeed />
                </ProtectedRoute>
            } />

            {/* Boîte à Idées Valnexo */}
            <Route path="/boite-a-idees" element={
                <ProtectedRoute>
                    <IdeaBox />
                </ProtectedRoute>
            } />

            {/* Inside VNX — réseau social interne (module isolé, lazy-load) */}
            <Route path="/inside-vnx" element={
                <ProtectedRoute>
                    <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="w-8 h-8 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin" /></div>}>
                        <InsideVNX />
                    </Suspense>
                </ProtectedRoute>
            } />
            
            <Route path="/pipeline" element={
                <ProtectedRoute>
                    <Pipeline />
                </ProtectedRoute>
            } />
            
            <Route path="/clients" element={
                <ProtectedRoute>
                    <Clients />
                </ProtectedRoute>
            } />
            
            <Route path="/prospects" element={
                <ProtectedRoute>
                    <Prospects />
                </ProtectedRoute>
            } />

            <Route path="/implication" element={
                <ProtectedRoute>
                    <Implication />
                </ProtectedRoute>
            } />

            <Route path="/prospects/new" element={
                <ProtectedRoute>
                    <ProspectForm />
                </ProtectedRoute>
            } />
            <Route path="/prospects/:id" element={
                <ProtectedRoute>
                    <ProspectDetail />
                </ProtectedRoute>
            } />
            
            <Route path="/prospects/:id/edit" element={
                <ProtectedRoute>
                    <ProspectForm />
                </ProtectedRoute>
            } />

            {/* Relevé d'étude sur place (natif, hors-ligne) */}
            <Route path="/prospects/:prospectId/study/:studyId" element={
                <ProtectedRoute>
                    <StudyFormPage />
                </ProtectedRoute>
            } />
            
            <Route path="/teams" element={
                <ProtectedRoute>
                    <TeamsV2 />
                </ProtectedRoute>
            } />
            
            <Route path="/academy" element={
                <ProtectedRoute>
                    <Academy />
                </ProtectedRoute>
            } />

            {/* Onboarding - Gestion des candidats (Admin / Super Admin) */}
            <Route path="/onboarding" element={
                <ProtectedRoute>
                    <OnboardingAdmin />
                </ProtectedRoute>
            } />
            
            <Route path="/academy/module/:moduleId" element={
                <ProtectedRoute>
                    <ModuleDetail />
                </ProtectedRoute>
            } />
            
            <Route path="/academy/admin" element={
                <ProtectedRoute>
                    <AcademyAdmin />
                </ProtectedRoute>
            } />
            
            <Route path="/academy/supervision" element={
                <ProtectedRoute>
                    <AcademySupervision />
                </ProtectedRoute>
            } />
            
            <Route path="/academy/manage" element={
                <ProtectedRoute>
                    <AcademyManage />
                </ProtectedRoute>
            } />
            
            {/* Archives EP - nouvelle route */}
            <Route path="/archives" element={
                <ProtectedRoute>
                    <CommunicationPage />
                </ProtectedRoute>
            } />
            
            {/* Formulaires d'Étude sur Place (CEE) - accès historique */}
            <Route path="/forms-cee" element={
                <ProtectedRoute>
                    <FormsCEEPage />
                </ProtectedRoute>
            } />
            
            {/* Outils & liens utiles */}
            <Route path="/outils" element={
                <ProtectedRoute>
                    <OutilsLiensUtilesPage />
                </ProtectedRoute>
            } />
            
            <Route path="/tasks" element={
                <ProtectedRoute>
                    <Tasks />
                </ProtectedRoute>
            } />
            
            <Route path="/calendar" element={
                <ProtectedRoute>
                    <CalendarPage />
                </ProtectedRoute>
            } />
            
            <Route path="/settings" element={
                <ProtectedRoute>
                    <Settings />
                </ProtectedRoute>
            } />
            
            {/* Fiche utilisateur / Profil équipe */}
            <Route path="/dashboard/team/user/:userId" element={
                <ProtectedRoute>
                    <UserProfile />
                </ProtectedRoute>
            } />

            {/* UserProfile routes temporarily disabled
            <Route path="/profile" element={
                <ProtectedRoute>
                    <UserProfile />
                </ProtectedRoute>
            } />
            
            <Route path="/profile/:userId" element={
                <ProtectedRoute>
                    <UserProfile />
                </ProtectedRoute>
            } />
            */}
            
            <Route path="/invoices" element={
                <ProtectedRoute>
                    <Invoices />
                </ProtectedRoute>
            } />
            
            <Route path="/billing" element={
                <ProtectedRoute>
                    <BillingV2 />
                </ProtectedRoute>
            } />
            
            <Route path="/ledger" element={
                <ProtectedRoute>
                    <CommissionLedger />
                </ProtectedRoute>
            } />
            
            {/* Redirection de l'ancienne page Stats vers Dashboard */}
            <Route path="/stats" element={
                <Navigate to="/dashboard" replace />
            } />
            
            <Route path="/support" element={
                <ProtectedRoute>
                    <SupportPage />
                </ProtectedRoute>
            } />
            
            <Route path="/reports" element={
                <ProtectedRoute>
                    <Reports />
                </ProtectedRoute>
            } />

            {/* Accountant Access (public routes) */}
            <Route path="/accountant-login" element={<AccountantLogin />} />
            <Route path="/accountant-dashboard" element={<AccountantDashboard />} />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to={defaultPath} replace />} />
            <Route path="*" element={<Navigate to={defaultPath} replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <ScrollRestoration />
                <AppRoutes />
                {/* MessengerWidget retiré - VTchatButton est utilisé à la place */}
                <Toaster position="top-center" offset={72} richColors />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
